import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Award, Calculator, BookOpen, Target, TrendingUp } from 'lucide-react';

const MoreInfo = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Calculator</span>
          </Link>
          <h1 className="text-4xl font-light tracking-tight text-gray-900 mb-4">
            More Information
          </h1>
          <p className="text-gray-500 text-lg">
            Comprehensive guide to heart rate training zones and calculation methods
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Introduction */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-start gap-4 mb-6">
              <BookOpen className="w-6 h-6 text-gray-400 mt-1" />
              <div>
                <h2 className="text-2xl font-light text-gray-900 mb-4">Understanding Heart Rate Training Zones</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Heart rate training zones are ranges of heart rate that correspond to different levels of exercise intensity. 
                  Training within specific zones allows athletes to target different physiological adaptations, from aerobic 
                  endurance to anaerobic power. Understanding these zones is crucial for effective training program design.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Different calculation methods offer varying levels of accuracy and personalization. This guide explains 
                  each method, its scientific basis, and when to use it.
                </p>
              </div>
            </div>
          </section>

          {/* Lab Test Method */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-start gap-4 mb-6">
              <Award className="w-6 h-6 text-yellow-500 mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-2xl font-light text-gray-900">Lab Test (3-Zone Model)</h2>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                    Gold Standard 🥇
                  </span>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  The most accurate method for determining training zones uses ventilatory thresholds (VT1/VT2) or 
                  lactate thresholds (LT1/LT2) measured during a metabolic cart test or lactate threshold test.
                </p>
                
                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">How It Works</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  During a lab test, you perform an incremental exercise test while breathing into a metabolic cart 
                  or having blood lactate measured. The test identifies two key physiological breakpoints:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4 ml-4">
                  <li><strong>VT1/LT1 (Aerobic Threshold):</strong> The point where ventilation or lactate begins to increase above baseline. Below this threshold, you're primarily using fat as fuel.</li>
                  <li><strong>VT2/LT2 (Anaerobic Threshold):</strong> The point where lactate accumulates rapidly. Above this threshold, you're relying heavily on anaerobic metabolism.</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">The Three Zones</h3>
                <div className="space-y-4">
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 1: Moderate / Light</h4>
                    <p className="text-gray-600 text-sm">Below VT1. Primary fuel is fat. Comfortable conversation possible (Talk Test).</p>
                  </div>
                  <div className="border-l-4 border-yellow-500 pl-4">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 2: Heavy / Moderate</h4>
                    <p className="text-gray-600 text-sm">Between VT1 & VT2. Lactate steady state and aerobic power.</p>
                  </div>
                  <div className="border-l-4 border-red-500 pl-4">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 3: Severe / High</h4>
                    <p className="text-gray-600 text-sm">Above VT2. Lactate accumulation and anaerobic capacity.</p>
                  </div>
                </div>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Best For</h3>
                <p className="text-gray-600 leading-relaxed">
                  Professional or serious athletes with access to physiological testing facilities. This method provides 
                  the most accurate and personalized training zones based on your actual metabolic responses.
                </p>
              </div>
            </div>
          </section>

          {/* Joe Friel Method */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-start gap-4 mb-6">
              <Target className="w-6 h-6 text-gray-400 mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-2xl font-light text-gray-900">Joe Friel LTHR Method</h2>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-800">
                    Silver Standard 🥈
                  </span>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Developed by renowned triathlon coach Joe Friel, this method uses your Lactate Threshold Heart Rate 
                  (LTHR) to create a detailed 7-zone system. The percentages differ between running and cycling, reflecting 
                  the different physiological demands of each sport.
                </p>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Finding Your LTHR</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Perform a 30-minute solo time trial on a flat course. After a 10-minute warm-up, run or cycle as hard 
                  as you can sustain for 30 minutes. Your LTHR is the average heart rate from the last 20 minutes of 
                  this effort.
                </p>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">The Seven Zones</h3>
                <div className="space-y-3 mb-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 1: Recovery</h4>
                    <p className="text-gray-600 text-sm">Easy recovery, warming up, cool down.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 2: Aerobic</h4>
                    <p className="text-gray-600 text-sm">Endurance, long slow distance, base building.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 3: Tempo</h4>
                    <p className="text-gray-600 text-sm">Muscular endurance, sweet spot training.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 4: Subthreshold</h4>
                    <p className="text-gray-600 text-sm">High intensity, just below lactate threshold.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 5a: Superthreshold</h4>
                    <p className="text-gray-600 text-sm">Above threshold, VO2max development.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 5b: Aerobic Capacity</h4>
                    <p className="text-gray-600 text-sm">Anaerobic endurance, very hard efforts.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 5c: Anaerobic Capacity</h4>
                    <p className="text-gray-600 text-sm">Maximal effort, sprints, neuromuscular power.</p>
                  </div>
                </div>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Best For</h3>
                <p className="text-gray-600 leading-relaxed">
                  Competitive athletes who perform regular field tests. This method provides granular zones for precise 
                  high-intensity interval training prescription, with sport-specific adjustments for running and cycling.
                </p>
              </div>
            </div>
          </section>

          {/* Karvonen Method */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-start gap-4 mb-6">
              <Calculator className="w-6 h-6 text-orange-400 mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-2xl font-light text-gray-900">Karvonen (Heart Rate Reserve)</h2>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                    Bronze Standard 🥉
                  </span>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  The Karvonen method accounts for individual fitness levels by incorporating your resting heart rate. 
                  This makes it more personalized than simple age-based formulas.
                </p>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">The Formula</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="font-mono text-gray-900">
                    Target HR = ((Max HR - Resting HR) × %) + Resting HR
                  </p>
                </div>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Finding Your Values</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4 ml-4">
                  <li><strong>Resting HR:</strong> Measure your heart rate first thing in the morning, before getting out of bed, for several days and take the average.</li>
                  <li><strong>Max HR:</strong> This should be measured, not estimated. Perform a maximal effort test under supervision, or use your highest recorded heart rate during a race or hard workout.</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">The Five Zones</h3>
                <div className="space-y-3 mb-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 1: Recovery (50-60% HRR)</h4>
                    <p className="text-gray-600 text-sm">Very light intensity for active recovery.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 2: Aerobic (60-70% HRR)</h4>
                    <p className="text-gray-600 text-sm">Endurance building and fat burning.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 3: Tempo (70-80% HRR)</h4>
                    <p className="text-gray-600 text-sm">Aerobic capacity development.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 4: Threshold (80-90% HRR)</h4>
                    <p className="text-gray-600 text-sm">Lactate tolerance training.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">Zone 5: Maximum (90-100% HRR)</h4>
                    <p className="text-gray-600 text-sm">Peak performance and VO2max.</p>
                  </div>
                </div>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Best For</h3>
                <p className="text-gray-600 leading-relaxed">
                  Intermediate runners looking for more personalized zones than age-based formulas. Requires both 
                  maximum and resting heart rate measurements.
                </p>
              </div>
            </div>
          </section>

          {/* Tanaka Method */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-start gap-4 mb-6">
              <TrendingUp className="w-6 h-6 text-blue-400 mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-2xl font-light text-gray-900">Tanaka Formula</h2>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-800">
                    Base Estimation
                  </span>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  A scientifically derived regression equation that is generally more accurate than the traditional 
                  220-Age formula for a wider population, especially older adults.
                </p>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">The Formula</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="font-mono text-gray-900">
                    Max HR = 208 - (0.7 × Age)
                  </p>
                </div>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Research Basis</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Developed from a large-scale study, the Tanaka formula accounts for age-related changes in maximum 
                  heart rate more accurately than the Fox & Haskell formula. It's particularly useful for older adults 
                  where the 220-Age formula tends to underestimate maximum heart rate.
                </p>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Best For</h3>
                <p className="text-gray-600 leading-relaxed">
                  General fitness enthusiasts and older adults who don't have access to lab testing or field tests. 
                  Provides a reasonable estimate when actual measurements aren't available.
                </p>
              </div>
            </div>
          </section>

          {/* Fox & Haskell Method */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-start gap-4 mb-6">
              <Calculator className="w-6 h-6 text-red-400 mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-2xl font-light text-gray-900">Fox & Haskell Formula</h2>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-50 text-red-800">
                    Low / Discouraged ⛔
                  </span>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  The oldest and simplest formula for estimating maximum heart rate. While widely known, it's often 
                  inaccurate with a standard deviation of approximately 10-12 bpm.
                </p>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">The Formula</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="font-mono text-gray-900">
                    Max HR = 220 - Age
                  </p>
                </div>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Limitations</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4 ml-4">
                  <li>High variability: Can be off by 10-12 bpm or more</li>
                  <li>Doesn't account for individual fitness levels</li>
                  <li>Less accurate for older adults</li>
                  <li>Doesn't consider resting heart rate</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Best For</h3>
                <p className="text-gray-600 leading-relaxed">
                  Rough estimation only. Not recommended for serious training. Consider using the Tanaka formula or, 
                  better yet, measuring your actual maximum heart rate.
                </p>
              </div>
            </div>
          </section>

          {/* Training Tips */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-light text-gray-900 mb-6">Training Tips</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-900 mb-2">Consistency is Key</h3>
                <p className="text-gray-600">
                  Use the same method consistently to track your progress over time. Switching between methods can 
                  make it difficult to compare training zones.
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold text-gray-900 mb-2">Re-test Regularly</h3>
                <p className="text-gray-600">
                  As your fitness improves, your thresholds and maximum heart rate may change. Re-test every 3-6 months 
                  to ensure your zones remain accurate.
                </p>
              </div>
              <div className="border-l-4 border-yellow-500 pl-4">
                <h3 className="font-semibold text-gray-900 mb-2">Listen to Your Body</h3>
                <p className="text-gray-600">
                  Heart rate zones are guidelines, not strict rules. Pay attention to how you feel during training. 
                  If you feel like you're in the wrong zone, adjust accordingly.
                </p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="font-semibold text-gray-900 mb-2">Use Multiple Metrics</h3>
                <p className="text-gray-600">
                  Combine heart rate with perceived exertion (RPE) and pace/power for the most comprehensive training 
                  guidance. Heart rate can be affected by factors like heat, hydration, and fatigue.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MoreInfo;

